import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconUser, IconPhone, IconCheck, IconX, IconStar, IconArrowLeft, IconSearch,
  IconUserPlus, IconMail, IconUsersGroup, IconGift, IconCalendar, IconDeviceTabletStar,
} from '@tabler/icons-react';
import { Modal } from './ui';
import { useCustomerSearch, createCustomer } from '../hooks/useCustomerSearch';

/**
 * CustomerPopover — designed customer-attach flow for the POS.
 *
 * Two states:
 *   1. Lookup — header + selected card (if any) + search + Recent
 *      customers default list + match list as user types + no-match
 *      CTA + footer (Walk-in skip / + new customer).
 *   2. Create-new — back link + live avatar preview + name + phone +
 *      optional email + loyalty welcome card + Save CTA.
 *
 * The component carries enough product polish to feel like a real
 * feature, not a generic form. Phone-first for SA, with SA mobile
 * auto-format. Loyalty tier is shown inline so the cashier sees the
 * regular's status at a glance.
 *
 * Currently uses a stub directory; swap for GET /api/customers when
 * the backend is wired.
 */

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  loyaltyPoints?: number;
  lastVisit?: string;     // ISO date, or relative phrase
  totalSpentCents?: number;
}

// Phase 3.1 — the directory is now backed by /api/customers/search via
// useCustomerSearch(). The legacy STUB_DIRECTORY here has been removed.

interface Props {
  open: boolean;
  onClose: () => void;
  onAttach: (customer: Customer) => void;
  onClear: () => void;
  selectedCustomer?: Customer | null;
}

function formatPhone(raw: string) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function initialsOf(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/**
 * `tone` + `bg` are for the soft avatar tile (initials must read);
 * `solid` is the high-contrast pill — solid coloured bg + white text +
 * extrabold so the cashier sees tier at a glance.
 */
function tierFor(points = 0): {
  label: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  tone: string;
  bg: string;
  solid: string;
} {
  if (points >= 400) return { label: 'Platinum', tone: 'text-purple',  bg: 'bg-purple-soft',  solid: 'bg-purple text-[#FFFFFF]' };
  if (points >= 200) return { label: 'Gold',     tone: 'text-warning', bg: 'bg-warning-soft', solid: 'bg-warning text-[#FFFFFF]' };
  if (points >=  50) return { label: 'Silver',   tone: 'text-info',    bg: 'bg-info-soft',    solid: 'bg-info text-[#FFFFFF]' };
  return                    { label: 'Bronze',   tone: 'text-orange',  bg: 'bg-orange-soft',  solid: 'bg-orange text-[#FFFFFF]' };
}

function fmtR(cents: number) {
  return `R${(cents / 100).toFixed(0)}`;
}

// ---- SUB-COMPONENTS --------------------------------------------------------

function CustomerRow({ c, onAttach }: { c: Customer; onAttach: (c: Customer) => void }) {
  const tier = tierFor(c.loyaltyPoints);
  return (
    <button
      type="button"
      onClick={() => onAttach(c)}
      className="w-full flex items-center gap-4 px-5 py-5 rounded-lg bg-surface border-2 border-border hover:border-primary hover:bg-primary-soft transition-colors text-left"
    >
      <div className={[
        'w-12 h-12 rounded-full inline-flex items-center justify-center font-bold text-15 flex-shrink-0',
        tier.bg, tier.tone,
      ].join(' ')}>
        {initialsOf(c.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 leading-tight">
          <p className="text-15 font-bold text-heading truncate">{c.name}</p>
          <span className={`inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded ${tier.solid} shadow-sm`}>
            {tier.label}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1 leading-tight">
          <p className="text-14 font-bold text-heading tabular-nums">{formatPhone(c.phone)}</p>
          {c.lastVisit && (
            <p className="text-13 font-semibold text-gray-700 inline-flex items-center gap-1">
              <IconCalendar size={13} stroke={2} /> {c.lastVisit}
            </p>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="inline-flex items-center gap-1 text-16 font-bold text-orange leading-tight">
          <IconStar size={16} stroke={2} fill="currentColor" />
          {c.loyaltyPoints ?? 0}
        </p>
        {typeof c.totalSpentCents === 'number' && c.totalSpentCents > 0 && (
          <p className="text-13 font-bold text-heading tabular-nums mt-1 leading-tight">{fmtR(c.totalSpentCents)} spent</p>
        )}
      </div>
    </button>
  );
}

// ---- MAIN ------------------------------------------------------------------

export default function CustomerPopover({ open, onClose, onAttach, onClear, selectedCustomer }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [step, setStep] = useState<'lookup' | 'create-new'>('lookup');
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newDob, setNewDob] = useState('');
  const [welcomeSms, setWelcomeSms] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Live customer directory backed by the API (Phase 3.1). Empty query
  // returns recent customers; any query searches name / phone / member.
  const { customers, loading } = useCustomerSearch(query);

  const reset = () => {
    setQuery('');
    setStep('lookup');
    setNewName('');
    setNewPhone('');
    setNewEmail('');
    setNewDob('');
    setWelcomeSms(true);
    setCreateError(null);
    setCreating(false);
  };
  const close = () => { reset(); onClose(); };

  const RECENT = query.trim() === '' ? customers.slice(0, 4) : [];
  const matches = query.trim() === '' ? [] : customers.slice(0, 6);

  const startCreateNew = () => {
    setStep('create-new');
    const digits = query.replace(/\D/g, '');
    if (digits.length >= 3) setNewPhone(digits);
    else if (query.trim()) setNewName(query.trim());
  };

  const saveNew = async () => {
    if (!newName.trim() || creating) return;
    setCreateError(null);
    setCreating(true);
    try {
      const customer = await createCustomer({
        name: newName.trim(),
        phone: newPhone.replace(/\D/g, ''),
        email: newEmail.trim() || undefined,
        dob: newDob || undefined,
      });
      onAttach(customer);
      close();
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={close} size="lg" hideClose>
      {step === 'lookup' && (
        <>
          {/* Header */}
          <div className="flex items-start gap-4 mb-6 pb-5 border-b border-border">
            <div className="w-12 h-12 rounded-lg bg-primary-soft text-primary inline-flex items-center justify-center flex-shrink-0">
              <IconUsersGroup size={22} stroke={2} />
            </div>
            <div className="flex-1">
              <h3 className="text-18 font-bold text-heading leading-tight">Attach a customer</h3>
              <p className="text-14 font-medium text-gray-700 mt-1 leading-snug">
                Track loyalty points, order history, and personalised offers.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <IconX size={18} stroke={2} />
            </button>
          </div>

          {/* Currently attached */}
          {selectedCustomer && (() => {
            const tier = tierFor(selectedCustomer.loyaltyPoints);
            return (
              <div className="mb-6 p-4 rounded-lg bg-primary-soft border-2 border-primary">
                <div className="flex items-start gap-4">
                  <div className={[
                    'w-14 h-14 rounded-full inline-flex items-center justify-center font-extrabold text-16 flex-shrink-0',
                    'bg-primary text-[#FFFFFF] shadow-sm',
                  ].join(' ')}>
                    {initialsOf(selectedCustomer.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5">
                      <p className="text-16 font-bold text-heading truncate">{selectedCustomer.name}</p>
                      <span className={`inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded ${tier.solid} shadow-sm`}>
                        {tier.label}
                      </span>
                    </div>
                    <p className="text-14 font-semibold text-gray-700 tabular-nums mt-1">{formatPhone(selectedCustomer.phone)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="inline-flex items-center gap-1 text-14 font-extrabold text-warning">
                        <IconStar size={14} stroke={2} fill="currentColor" />
                        {selectedCustomer.loyaltyPoints ?? 0} pts
                      </span>
                      {selectedCustomer.lastVisit && (
                        <span className="inline-flex items-center gap-1 text-13 font-medium text-gray-700">
                          <IconCalendar size={13} stroke={2} />
                          {selectedCustomer.lastVisit}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { onClear(); close(); }}
                    className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-surface border border-border text-13 font-semibold text-gray-700 hover:bg-danger-soft hover:text-danger hover:border-danger/30 transition-colors flex-shrink-0"
                    title="Remove customer"
                  >
                    <IconX size={14} stroke={2.5} /> Remove
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Search */}
          <label className="block text-14 font-bold text-heading mb-2.5">
            Find by phone or name
          </label>
          <div className="relative mb-6">
            <IconSearch size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type 082… or a name"
              className="w-full h-12 pl-12 pr-4 rounded-lg bg-surface border-2 border-border focus:border-primary text-14 font-medium text-heading placeholder-gray-400 outline-none transition-colors"
            />
          </div>

          {/* Default state — recent customers */}
          {query.trim() === '' && (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-12 font-extrabold uppercase tracking-wider text-gray-700">Recent customers</h4>
                <span className="flex-1 h-px bg-border" />
              </div>
              {loading && RECENT.length === 0 ? (
                <p className="text-13 italic text-gray-700 mb-6">Loading…</p>
              ) : RECENT.length === 0 ? (
                <p className="text-13 italic text-gray-700 mb-6">No customers yet — tap "+ New customer" to add the first one.</p>
              ) : (
                <ul className="space-y-2.5 mb-6">
                  {RECENT.map((c) => (
                    <li key={c.id}>
                      <CustomerRow c={c} onAttach={(cust) => { onAttach(cust); close(); }} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* Match results */}
          {query.trim() !== '' && matches.length > 0 && (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-12 font-extrabold uppercase tracking-wider text-gray-700">
                  {matches.length} match{matches.length === 1 ? '' : 'es'}
                </h4>
                <span className="flex-1 h-px bg-border" />
              </div>
              <ul className="space-y-2.5 mb-6">
                {matches.map((c) => (
                  <li key={c.id}>
                    <CustomerRow c={c} onAttach={(cust) => { onAttach(cust); close(); }} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* No matches */}
          {query.trim() !== '' && matches.length === 0 && (
            <div className="mb-5 p-5 rounded-lg bg-light border border-border-soft text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-surface inline-flex items-center justify-center text-gray-400 mb-3 border border-border">
                <IconUserPlus size={20} stroke={1.75} />
              </div>
              <p className="text-15 font-bold text-heading">No matching customer</p>
              <p className="text-13 font-medium text-gray-700 mt-1.5">First time visiting? Sign them up to start earning loyalty points.</p>
              <button
                type="button"
                onClick={startCreateNew}
                className="mt-3 inline-flex items-center gap-1.5 h-11 px-4 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover transition-colors"
              >
                <IconUserPlus size={14} stroke={2.5} />
                Add as new customer
              </button>
            </div>
          )}

          {/* Footer — three actions: walk-in / add it myself / pass to
              customer (the "flip the iPad" loyalty signup). Pass is the
              hero action because it's the moment that makes the brand. */}
          <div className="pt-5 border-t border-border space-y-3">
            <button
              type="button"
              onClick={() => { close(); navigate('/onboard-customer'); }}
              className="w-full h-14 inline-flex items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#dd0000] to-[#a30606] text-[#FFFFFF] text-15 font-bold hover:brightness-110 shadow-lg active:scale-[0.99] transition-all"
            >
              <IconDeviceTabletStar size={20} stroke={2.25} />
              Pass to customer to sign up
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { onClear(); close(); }}
                className="flex-1 h-12 inline-flex items-center justify-center rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors"
              >
                Continue as walk-in
              </button>
              <button
                type="button"
                onClick={startCreateNew}
                className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-md bg-light text-heading text-14 font-bold border-2 border-border hover:border-primary hover:text-primary transition-colors"
              >
                <IconUserPlus size={16} stroke={2.5} />
                I'll add it
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'create-new' && (
        <>
          {/* Header */}
          <div className="flex items-start gap-4 mb-6 pb-5 border-b border-border">
            <div className="w-12 h-12 rounded-lg bg-success-soft text-success inline-flex items-center justify-center flex-shrink-0">
              <IconUserPlus size={22} stroke={2} />
            </div>
            <div className="flex-1">
              <h3 className="text-18 font-bold text-heading leading-tight">Sign up new customer</h3>
              <p className="text-14 font-medium text-gray-700 mt-1 leading-snug">
                Creates a profile and enrols them in the HL Coffee Shop loyalty programme.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <IconX size={18} stroke={2} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setStep('lookup')}
            className="inline-flex items-center gap-1.5 text-14 font-semibold text-gray-700 hover:text-primary mb-5 transition-colors"
          >
            <IconArrowLeft size={16} stroke={2.25} /> Back to search
          </button>

          {/* Live profile preview — eyebrow + card. Empty state feels
              intentional (clean neutral avatar, not dashed). BRONZE pill
              only appears once a name has been entered, so the empty
              state isn't a mix of live + placeholder content. */}
          <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mb-2">
            Live preview
          </p>
          <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-light border border-border">
            <div className={[
              'w-14 h-14 rounded-full inline-flex items-center justify-center font-bold text-18 flex-shrink-0 transition-colors',
              newName.trim() ? 'bg-primary text-[#FFFFFF] shadow-sm' : 'bg-surface border border-border text-gray-400',
            ].join(' ')}>
              {newName.trim() ? initialsOf(newName) : <IconUser size={22} stroke={2} />}
            </div>
            <div className="flex-1 min-w-0 leading-tight">
              <p className={`text-15 font-bold truncate ${newName.trim() ? 'text-heading' : 'text-gray-500 italic'}`}>
                {newName.trim() || 'Customer name will appear here'}
              </p>
              <p className={`text-13 font-semibold tabular-nums mt-1 ${newPhone ? 'text-gray-700' : 'text-gray-500'}`}>
                {newPhone ? formatPhone(newPhone) : 'Phone — for loyalty'}
              </p>
              {newEmail && <p className="text-13 font-medium text-gray-700 truncate mt-0.5">{newEmail}</p>}
            </div>
            {newName.trim() && (
              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded bg-orange text-[#FFFFFF] shadow-sm flex-shrink-0">
                Bronze
              </span>
            )}
          </div>

          {/* Form fields */}
          <div className="space-y-5 mb-6">
            <div>
              <label className="block text-14 font-bold text-heading mb-2">Customer name</label>
              <div className="relative">
                <IconUser size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Aaliyah Khan"
                  className="w-full h-12 pl-11 pr-4 rounded-lg bg-surface border-2 border-border focus:border-primary text-14 text-heading placeholder-gray-400 outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-14 font-bold text-heading mb-2">
                Phone number <span className="text-13 text-gray-600 font-semibold">— for loyalty</span>
              </label>
              <div className="relative">
                <IconPhone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={formatPhone(newPhone)}
                  onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="082 123 4567"
                  className="w-full h-12 pl-11 pr-4 rounded-lg bg-surface border-2 border-border focus:border-primary text-14 text-heading placeholder-gray-400 outline-none transition-colors tabular-nums"
                />
              </div>
            </div>

            <div>
              <label className="block text-14 font-bold text-heading mb-2">
                Email <span className="text-13 text-gray-600 font-semibold">— optional, for digital receipts</span>
              </label>
              <div className="relative">
                <IconMail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="customer@example.co.za"
                  className="w-full h-12 pl-11 pr-4 rounded-lg bg-surface border-2 border-border focus:border-primary text-14 text-heading placeholder-gray-400 outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-14 font-bold text-heading mb-2">
                Date of birth <span className="text-13 text-gray-600 font-semibold">— optional, for the birthday freebie</span>
              </label>
              <div className="relative">
                <IconCalendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={newDob}
                  onChange={(e) => setNewDob(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 rounded-lg bg-surface border-2 border-border focus:border-primary text-14 text-heading placeholder-gray-400 outline-none transition-colors tabular-nums"
                />
              </div>
            </div>
          </div>

          {/* Loyalty welcome card */}
          <div className="mb-6 p-5 rounded-lg bg-success-soft border-2 border-success/40 flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-success text-[#FFFFFF] inline-flex items-center justify-center flex-shrink-0 shadow-sm">
              <IconGift size={20} stroke={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-16 font-bold uppercase tracking-wide text-success leading-tight">
                Welcome to the loyalty programme
              </p>
              <p className="text-13 font-medium text-heading mt-1.5 leading-snug">
                Earn <span className="font-bold">1 point per Rand</span> spent.
                Redeem at <span className="font-bold">100 pts = R10 reward</span>.
                Tier upgrades unlock free drinks and birthday treats.
              </p>
              <label className="inline-flex items-center gap-3 mt-4 cursor-pointer select-none group">
                {/* Custom checkbox — native one renders the check in
                    user-agent default colour (often black on green) on
                    Chrome/Safari, which looked broken. This guarantees
                    a white tick on the brand green. */}
                <span
                  className={[
                    'w-5 h-5 rounded inline-flex items-center justify-center transition-colors flex-shrink-0',
                    welcomeSms
                      ? 'bg-success text-[#FFFFFF]'
                      : 'bg-surface border-2 border-gray-400 group-hover:border-success',
                  ].join(' ')}
                >
                  {welcomeSms && <IconCheck size={14} stroke={3.5} />}
                </span>
                <input
                  type="checkbox"
                  checked={welcomeSms}
                  onChange={(e) => setWelcomeSms(e.target.checked)}
                  className="sr-only"
                />
                <span className="text-13 text-heading font-bold">Send welcome SMS with loyalty card link</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 pt-5 border-t border-border">
            <button
              type="button"
              onClick={() => setStep('lookup')}
              className="h-12 px-5 inline-flex items-center justify-center rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors"
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              type="button"
              disabled={!newName.trim() || creating}
              onClick={saveNew}
              className="h-12 px-5 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none transition-colors"
            >
              <IconCheck size={18} stroke={2.75} />
              {creating ? 'Saving…' : 'Save & attach'}
            </button>
          </div>

          {createError && (
            <p className="mt-3 text-13 font-semibold text-danger text-center">{createError}</p>
          )}

        </>
      )}
    </Modal>
  );
}

